import { useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from '../Toast';
import { authFetch, withFileAuth, thumbnailUrl } from '../../utils/secureFiles';
import { uniqueFileNames } from '../../utils/media';

const fetchBlob = async (file) => {
    const res = await authFetch(file.downloadUrl || file.fileUrl);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
};

// 썸네일 1장: 크기 정보가 있으면 자리를 미리 잡아 이미지 로드 후 스크롤이 튀지 않게 한다
const MediaTile = ({ file, single, onOpen, onLoad, overlay }) => {
    const [useOriginal, setUseOriginal] = useState(false);
    const isVideo = file.mimetype.startsWith('video/');
    const sizeStyle = single && file.width && file.height ? { aspectRatio: `${file.width} / ${file.height}` } : undefined;
    return (
        <div className="media-item" style={sizeStyle} onClick={onOpen}>
            {isVideo ? (
                // preload="metadata": 전체 영상을 받지 않고 첫 프레임/길이 정보만 요청
                <video src={`${withFileAuth(file.fileUrl)}#t=0.1`} className="media-thumb" muted playsInline preload="metadata" onLoadedMetadata={onLoad} />
            ) : (
                <img
                    src={useOriginal ? withFileAuth(file.fileUrl) : thumbnailUrl(file.fileUrl)}
                    alt={file.originalName}
                    className="media-thumb"
                    width={file.width}
                    height={file.height}
                    loading="lazy"
                    decoding="async"
                    draggable="false"
                    onLoad={onLoad}
                    onError={() => { if (!useOriginal) setUseOriginal(true); }}
                />
            )}
            {isVideo && <div className="video-badge">▶</div>}
            {overlay}
        </div>
    );
};

export const MediaBundle = ({ files, onOpenLightbox, onImageLoad }) => {
    const [isDownloading, setIsDownloading] = useState(false);

    const downloadAll = async () => {
        if (isDownloading) return;
        setIsDownloading(true);
        try {
            if (files.length === 1) {
                saveAs(await fetchBlob(files[0]), files[0].originalName);
                return;
            }
            const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const folder = new JSZip().folder(`download_${dateStr}`);
            const names = uniqueFileNames(files.map(f => f.originalName));
            const results = [];
            // 대용량 파일을 한꺼번에 메모리에 올리지 않도록 3개씩 나눠 다운로드
            for (let i = 0; i < files.length; i += 3) {
                const batch = files.slice(i, i + 3);
                results.push(...await Promise.allSettled(batch.map(fetchBlob)));
            }
            results.forEach((result, idx) => {
                if (result.status === 'fulfilled') folder.file(names[idx], result.value);
                else console.error(`Failed to load ${files[idx].originalName}`, result.reason);
            });
            saveAs(await folder.generateAsync({ type: 'blob' }), `download_${dateStr}.zip`);
            const failed = results.filter(r => r.status === 'rejected').length;
            if (failed) toast.error(`${failed}개 파일을 받지 못했습니다.`);
        } catch { toast.error('다운로드 중 오류가 발생했습니다.'); }
        finally { setIsDownloading(false); }
    };

    const gridClass = `grid-${Math.min(files.length, 4)}`;
    return (
        <div className="media-bundle-container">
            <div className={`media-grid ${gridClass}`}>
                {files.slice(0, 4).map((file, index) => (
                    <MediaTile
                        key={file.fileUrl}
                        file={file}
                        single={files.length === 1}
                        onOpen={() => onOpenLightbox(index)}
                        onLoad={onImageLoad}
                        overlay={index === 3 && files.length > 4 ? <div className="more-overlay">+{files.length - 3}</div> : null}
                    />
                ))}
            </div>
            <div className="bundle-actions">
                <button className="bundle-download-all" onClick={downloadAll} disabled={isDownloading}>
                    {isDownloading ? (files.length === 1 ? '💾 저장 중...' : '📦 압축 중...') : `💾 ${files.length === 1 ? '저장' : `전체 저장 (${files.length}개)`}`}
                </button>
            </div>
        </div>
    );
};
